import { useState } from "react";
import { useNotify, useRecordContext, useRefresh } from "ra-core";
import { Link } from "react-router";
import {
  Building2,
  ExternalLink,
  Flame,
  Globe,
  Layers,
  Linkedin,
  Loader2,
  MapPin,
  Search,
  Sparkles,
  TrendingUp,
  UserPlus,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company } from "../types";

export function CompanyWaterfallButton({
  company: propCompany,
  className,
}: {
  company?: Company | null;
  className?: string;
}) {
  const record = useRecordContext<Company>();
  const company = propCompany || record;
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  if (!company) return null;

  const handleEnrich = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const res = await supabase.functions.invoke("company-waterfall-enricher", {
        body: {
          company_id: company.id,
          name: company.name,
          website: company.website || undefined,
          linkedin_url: company.linkedin_url || undefined,
        },
      });

      if (res.error) throw new Error(res.error.message);
      if (!res.data?.success) throw new Error(res.data?.error || "Enrichment failed");

      notify(`Enriched ${company.name} with Clay waterfall intelligence!`, {
        type: "success",
      });
      refresh();
    } catch (err: any) {
      console.error("Company enrichment error:", err);
      notify(`Enrichment error: ${err.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      disabled={loading}
      onClick={handleEnrich}
      className={`inline-flex items-center gap-1.5 h-8 text-xs font-semibold bg-gradient-to-r from-amber-500 to-indigo-600 text-white hover:opacity-90 transition-opacity shadow-xs cursor-pointer ${className || ""}`}
    >
      {loading ? (
        <>
          <Loader2 className="size-3.5 animate-spin" />
          <span>Waterfall Enriching...</span>
        </>
      ) : (
        <>
          <Zap className="size-3.5 fill-amber-300 text-amber-200" />
          <span>Clay Waterfall Enrich</span>
        </>
      )}
    </Button>
  );
}

export function CompanyWaterfallCard({
  company: propCompany,
}: {
  company?: Company | null;
}) {
  const record = useRecordContext<Company>();
  const company = propCompany || record;

  if (!company) return null;

  const contextData = (company.context_links as any) || {};
  const personas = Array.isArray(contextData.suggested_personas)
    ? contextData.suggested_personas
    : [];
  const enneaAngle = contextData.ennea_value_angle || null;
  const priorities = Array.isArray(contextData.key_priorities)
    ? contextData.key_priorities
    : [];

  return (
    <div className="space-y-4 mb-4">
      {/* Header bar with Clay Button */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border bg-muted/20">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg">
            <Zap className="size-4 fill-amber-400 text-amber-500" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <span>Clay Waterfall Intelligence</span>
              {contextData.enriched_at && (
                <Badge variant="outline" className="text-[10px] h-4 font-normal py-0">
                  Enriched
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {company.revenue ? `${company.revenue} ARR` : "Revenue pending"} •{" "}
              {company.city && company.country ? `${company.city}, ${company.country}` : "HQ Global"}
            </div>
          </div>
        </div>

        <CompanyWaterfallButton company={company} />
      </div>

      {/* Ennea International Value Angle Banner */}
      {enneaAngle && (
        <div className="p-3.5 rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/20 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
            <Sparkles className="size-3.5 text-indigo-500" />
            <span>Ennea International Strategic Value Hook</span>
          </div>
          <p className="text-xs text-foreground leading-relaxed font-medium">
            "{enneaAngle}"
          </p>
          {priorities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {priorities.map((p: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-[10px]">
                  {p}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suggested Decision-Maker Buyer Personas */}
      {personas.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <UserPlus className="size-3.5 text-indigo-500" />
            <span>Target Decision-Maker Personas to Prospect</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {personas.map((persona: any, idx: number) => {
              const googleLinkedinSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(
                persona.search_query || `site:linkedin.com/in "${company.name}" "${persona.role_title}"`
              )}`;

              return (
                <div
                  key={idx}
                  className="p-3 rounded-lg border bg-card hover:border-indigo-300 transition-colors flex flex-col justify-between space-y-2 text-xs"
                >
                  <div className="space-y-1">
                    <Badge variant="outline" className="text-[10px] text-indigo-600 font-semibold">
                      {persona.department || "Executive"}
                    </Badge>
                    <div className="font-semibold text-foreground line-clamp-1">
                      {persona.role_title}
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-2">
                      {persona.target_angle}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t gap-1">
                    <a
                      href={googleLinkedinSearchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-sky-600 hover:text-sky-500 font-medium"
                    >
                      <Linkedin className="size-3" />
                      <span>Find on LI</span>
                      <ExternalLink className="size-2.5 opacity-70" />
                    </a>

                    <Link
                      to="/contacts/create"
                      state={{
                        record: {
                          company_id: company.id,
                          title: persona.role_title,
                        },
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-500 font-medium"
                    >
                      <UserPlus className="size-3" />
                      <span>Add</span>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
